import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProjectGroupComponent } from '../list/project-group/project-group.component';
import { toProjectVms, sanitizeProjectDetails } from '../list/list-grouping.util';
import { ProjectService } from '../services/project.service';
import { AtpSignAcceptedDialogComponent } from '../atp-sign-accepted-dialog/atp-sign-accepted-dialog.component';
import { ModalService } from '../services/modal.service';
import { DeclineSuccessComponent } from '../decline-success/decline-success.component';
import { AcceptedOfferedProposalDialogComponent } from '../accepted-offered-proposal-dialog/accepted-offered-proposal-dialog.component';
import { CommonUtilityServiceService } from '../services/common-utility-service.service';
import { AppService } from '../services/app.service';
import { scrollToProjectWhenReady } from '../helpers/scroll-to-project.util';
import { SignedAtpSiteVisit } from '../model/signed-atp-site-visit';
import { BatchDeclineSummary } from '../model/batch-decline';

@Component({
  standalone: true,
  selector: 'info-proposal',
  imports: [
    CommonModule,
    ProjectGroupComponent,
    AtpSignAcceptedDialogComponent,
    DeclineSuccessComponent,
    AcceptedOfferedProposalDialogComponent,
  ],
  templateUrl: './proposals-page.component.html',
  styleUrl: './proposals-page.component.scss',
})
export class ProposalsPage implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  data: any = null;
  atpModalHeaderTitle: string = 'ATP Signed & Project Active';
  atpModalBody: string =
    'You have signed the ATP and the project can be viewed from the Active tab.';
  isAtpSignAccepted: boolean = false;
  // Site visits the last ATP signature on this page left schedulable, relayed
  // up from the product line that signed. Cleared when the confirmation
  // closes, so a later signature that opened nothing cannot show a stale
  // offer. Held on the page, never in a service — a root-provided subject
  // would keep the previous result across navigations.
  signedAtpSiteVisits: SignedAtpSiteVisit[] = [];
  isDeclineSuccessActive: boolean = false;
  declineBatch: BatchDeclineSummary | null = null;
  isOfferAcceptedActive: boolean = false;
  // Plurality of the accept that opened the confirmation, carried on the
  // ModalService emission rather than hard-bound in the template — a bulk
  // accept-all and a single inline accept both land on this same dialog.
  isOfferAcceptedMultiple: boolean = false;
  isFixedPrice: boolean = false;
  isAtp: boolean = false;
  siteVisitConfirmationActive: boolean = false;
  address: any = { streetAddress: '', cityStateZip: '' };
  loading: boolean = true;

  constructor(
    private projectService: ProjectService,
    private modalService: ModalService,
    private utilityService: CommonUtilityServiceService,
    private appService: AppService
  ) {}

  // Set when the vendor arrived from the Dashboard work map's "View project"
  // (My Offers pin) — the project to scroll to + flash once the list renders.
  private scrollTargetProjectId: string | null = null;
  // Stops the scroll-to-project rAF loop if the component is torn down before
  // the target anchor renders (avoids frames ticking + a flash timeout firing
  // after destroy).
  private destroyed = false;

  ngOnInit() {
    // Read the navigation state BEFORE fetchData(): the ProjectService cache is
    // a ReplaySubject, so a cached emission fires synchronously inside fetchData().
    this.scrollTargetProjectId =
      (window.history.state && window.history.state['scrollToProjectId']) ??
      null;
    this.fetchData();
    this.modalService.isAtpSignAcceptedModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => {
        this.isAtpSignAccepted = isOpen;
      });
    this.modalService.isDeclineModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((detail) => {
        // CON-148 #6 - outside the guard on purpose; see invitations-page for the full reasoning.
        // Guarded, this only ever wrote `true`, so the second decline never re-opened the dialog.
        this.isDeclineSuccessActive = detail.isOpen;
        if (detail.isOpen) {
          this.declineBatch = detail.batch ?? null;
          this.isFixedPrice = detail.isFixedPrice;
          this.isAtp = detail.isAtp;
          if (detail.siteDetail.site) {
            this.address = this.utilityService.formatAddress(
              detail.siteDetail
            );
          }
        }
      });
    this.modalService.isOfferAcceptedModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((offer) => {
        this.isOfferAcceptedActive = offer.open;
        this.isOfferAcceptedMultiple = offer.multiple;
      });
    this.appService.setRedirectUrl('');
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByProjectId(index: number, item: any): string {
    return item?.projectId ?? `${index}`;
  }

  toggleCloseAtpSignAcceptedModal() {
    this.signedAtpSiteVisits = [];
    this.modalService.toggleCloseAtpSignAcceptedModal();
  }

  toggleCloseDeclineSuccessModal() {
    this.modalService.toggleCloseDeclineSuccessModal();
  }

  toggleCloseOfferAcceptedModal() {
    this.modalService.toggleCloseOfferAcceptedModal();
  }

  fetchData() {
    this.projectService
      .fetchData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        const filtered = sanitizeProjectDetails(result)
          .map((parent: any) => ({
            ...parent,
            sites: parent.sites.filter(
              (child: any) =>
                child?.proposal?.status === 'SUBMITTED' ||
                child?.proposal?.status === 'PROPOSAL' ||
                child?.proposal?.status === 'BID_LOST' ||
                child?.proposal?.status === 'NO_RESPONSE' ||
                child?.proposal?.status === 'BID_WON'
            ),
          }))
          .filter(
            (parent: any) =>
              parent.sites.length > 0 &&
              parent.projectId &&
              !parent.sites.every(
                (siteDetail: any) =>
                  siteDetail.proposal?.status === 'BID_LOST' ||
                  siteDetail.proposal?.status === 'NO_RESPONSE'
              )
          );
        // Group the flat project/site/proposal rows into the nested
        // Project → Site → Product view-model the CONNECT 2.0 cards render.
        this.data = toProjectVms(filtered);
        this.loading = false;

        if (this.scrollTargetProjectId) {
          const target = this.scrollTargetProjectId;
          this.scrollTargetProjectId = null; // one-shot — don't re-scroll on refresh
          scrollToProjectWhenReady(target, () => this.destroyed);
        }
      });
  }
}
